import type { TFile } from '../types/common.type';

export enum ArticleArchitectProgressPhase {
  Reading = 'reading',
  Pages = 'pages',
  Extracting = 'extracting',
  Words = 'words',
  // * Per-ticket classifier progress for a TopDesk backfill estimate, emitted only
  // * when the company has the ticket classifier switched on. Carries
  // * `processed`/`total`, never emitted by the upload/BCC flow.
  Classifying = 'classifying',
  // * Per-incident embedding progress for a TopDesk backfill run. Carries
  // * `processed`/`total`, never emitted by the upload/BCC flow.
  Embedding = 'embedding',
  Generating = 'generating',
  ArticleDrafted = 'article-drafted',
  CheckingConflicts = 'checking-conflicts',
  ConflictDetected = 'conflict-detected',
  Conflicts = 'conflicts',
  FileFailed = 'file-failed',
  FileNoContent = 'file-no-content',
}

export interface ArticleArchitectProgressData {
  phase: ArticleArchitectProgressPhase;
  filename?: string;
  pageCount?: number;
  wordCount?: number;
  title?: string;
  reason?: string;
  // Title of the existing published article that conflicts with the new article
  conflictingTitle?: string;
  // Live progress counts for a TopDesk backfill run: records processed/total for
  // the Classifying and Embedding phases, and the article number/total for the ArticleDrafted phase.
  processed?: number;
  total?: number;
  // * Set only on the TopDesk manual generation path, so the frontend can drop a
  // * progress event that belongs to a different run. Absent on the upload and BCC
  // * flows, which never filter on it.
  runId?: string;
}

export interface ArticleArchitectCompletedData {
  uploadId: string;
  articlesGenerated: number;
  conflictsDetected?: number;
  couldNotProcess?: number;
  // * TopDesk generation only: tickets that ended up in an article, and every
  // * ingested ticket of the run.
  generatedTicketCount?: number;
  ingestedTicketCount?: number;
}

export interface ArticleArchitectErrorData {
  errorCode: string;
  errorData?: unknown;
  // * Set only on the TopDesk manual generation path (see ArticleArchitectProgressData).
  runId?: string;
}

// * Terminal payload for a successful TopDesk YTD incident pull. The pull writes
// * a raw artifact and generates no articles, so it reports a retrieved incident
// * count only. The error case reuses ArticleArchitectErrorData.
export interface ArticleArchitectTopdeskPullCompletedData {
  incidentCount: number;
}

// * Heartbeat payload reported while a TopDesk YTD incident pull is still
// * running, so the frontend can show live progress against the known total.
export interface ArticleArchitectTopdeskPullProgressData {
  processed: number;
  total: number;
}

export interface ArticleArchitectStartPayload {
  companyId: string;
  files: TFile[];
}

// * Start payload for the manual TopDesk draft-generation flow. The role split
// * (estimate-then-confirm vs straight-through) is decided server-side, so the
// * client sends only the company.
export interface ArticleArchitectTopdeskGenerationStartPayload {
  companyId: string;
}

// * Confirm payload for the superAdmin phase-B run. The runId ties the confirm
// * back to the exact estimate the superAdmin approved.
export interface ArticleArchitectTopdeskGenerationConfirmPayload {
  companyId: string;
  runId: string;
}

// * Cancel payload for the estimate leg (phase-A embedding). The source is
// * optional and defaults to TopDesk server-side; it is typed as a plain string
// * so this shared DTO stays free of a @conversed/rest dependency.
export interface ArticleArchitectTopdeskGenerationCancelPayload {
  companyId: string;
  source?: string;
}

// * Estimate-ready payload emitted after phase A. articleCount equals clusterCount
// * for the clustered strategy - one draft per cluster - and is sent explicitly so
// * the frontend does not encode that assumption.
export interface ArticleArchitectTopdeskGenerationEstimateData {
  runId: string;
  wordTotal: number;
  clusterCount: number;
  articleCount: number;
}

export interface ConflictScanProgressData {
  processedCount: number;
  totalCount: number;
  title: string;
}

export interface ConflictScanCompletedData {
  articlesScanned: number;
  conflictsDetected: number;
}
